import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';

import { ControlRecordComponent } from '@shared/components/control/control-record/control-record.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  startWith,
} from 'rxjs';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getDefaultGroups } from '@shared/utils/control';
import { WINDOW } from '@shared/providers/window.provider';
import { PolymorpheusContent } from '@tinkoff/ng-polymorpheus';
import {
  TuiButtonModule,
  TuiScrollbarModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { TuiBadgeModule, TuiIconModule } from '@taiga-ui/experimental';
import { TuiLetModule } from '@taiga-ui/cdk';
import { TuiInputModule } from '@taiga-ui/kit';
import {
  ControlGroupButton,
  ControlRecord,
  ControlRecordMessageType,
} from '../../../../types/control';

@Component({
  selector: 'app-control-panel',
  imports: [
    CdkVirtualScrollViewport,
    ControlRecordComponent,
    CdkVirtualForOf,
    CdkFixedSizeVirtualScroll,
    ReactiveFormsModule,
    AsyncPipe,
    TuiButtonModule,
    TuiBadgeModule,
    TuiBadgeModule,
    TuiLetModule,
    TuiScrollbarModule,
    TuiIconModule,
    TuiInputModule,
    TuiTextfieldControllerModule,
  ],
  templateUrl: './control-panel.component.html',
  styleUrl: './control-panel.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ControlPanelComponent implements OnInit {
  @Input() errorAction!: PolymorpheusContent<unknown>;
  @Output() refresh = new EventEmitter();
  records$ = new BehaviorSubject<ControlRecord[]>([]);

  protected filter = new FormControl('');

  solvedMap$ = new BehaviorSubject<Map<ControlRecord, boolean>>(new Map());

  protected filteredRecords$ = combineLatest([
    this.records$,
    this.filter.valueChanges.pipe(
      startWith(''),
      map(search => search?.toLowerCase()),
    ),
    this.solvedMap$,
  ]).pipe(
    map(([items, search, solved]) => {
      const notMarkedAsSolved = items.filter(item => !solved.has(item));
      if (!search) {
        return notMarkedAsSolved;
      }
      return notMarkedAsSolved.filter(record => {
        const searchValue = [
          record.name,
          record.graph,
          record.message.text,
        ].join('-');
        return searchValue?.toLowerCase().includes(search);
      });
    }),
  );

  protected collapsed$ = new BehaviorSubject<boolean>(true);
  protected maximized$ = new BehaviorSubject<boolean>(false);

  readonly buttons: ControlGroupButton[] = [
    {
      name: 'error',
      title: 'Ошибки',
      icon: 'tuiIconCircleX',
      iconColor: 'red',
    },
    {
      name: 'warning',
      title: 'Предупреждения',
      icon: 'tuiIconTriangleAlert',
      iconColor: 'orange',
    },
    {
      name: 'info',
      title: 'Информация',
      icon: 'tuiIconInfo',
      iconColor: 'black',
    },
  ];

  groups$ = this.records$.pipe(
    map(records => {
      const groups = getDefaultGroups();
      records.forEach(record => {
        const type = record.message.type;
        groups[type].push(record);
      });
      return groups;
    }),
  );

  buttons$ = this.groups$.pipe(
    map(records => {
      return this.buttons.map(({ name, title, ...props }) => {
        return {
          name,
          ...props,
          title: `${title} (${records[name].length})`,
        };
      });
    }),
  );

  virtualScrollHeight$: Observable<number> = this.maximized$.pipe(
    map(maximized => {
      return maximized ? this.window.innerHeight / 1.7 : 150;
    }),
  );

  constructor(
    @Inject(DOCUMENT) private readonly doc: Document,
    @Inject(WINDOW) private readonly window: Window,
    private readonly destroyRef: DestroyRef,
  ) {}

  markAsSolved(record: ControlRecord) {
    const solvedMap = this.solvedMap$.value;
    solvedMap.set(record, true);
    this.solvedMap$.next(solvedMap);
  }

  openPanel() {
    this.collapsed$.next(false);
  }

  closePanel() {
    this.collapsed$.next(true);
  }

  maximize() {
    this.maximized$.next(!this.maximized$.value);
  }

  @Input({ required: true }) set records(records: ControlRecord[]) {
    this.setRecords(records);
    this.resetSolvedMap();
  }

  resetSolvedMap() {
    this.solvedMap$.next(new Map());
  }

  setRecords(records: ControlRecord[]) {
    const cloned = structuredClone(records);
    const typeOrder: Record<ControlRecordMessageType, number> = {
      error: 0,
      warning: 1,
      info: 2,
    };
    cloned.sort(
      (a, b) => typeOrder[a.message.type] - typeOrder[b.message.type],
    );
    this.records$.next(cloned);
  }

  addContainerExtraPadding() {
    this.collapsed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(collapsed => {
        const elementHTMLCollection = this.doc.getElementsByClassName(
          'detail-page__container',
        );
        const container = elementHTMLCollection[0] as HTMLDivElement;
        if (collapsed) {
          container.style.paddingBottom = '0px';
        } else {
          container.style.paddingBottom = '220px';
        }
      });
  }

  ngOnInit() {
    this.addContainerExtraPadding();
  }
}
